// src/components/common/FilterPanel/FilterPanel.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Filter, X, ChevronDown, FileSpreadsheet, Search, Check } from 'lucide-react';
import Dropdown from '../../ui/Dropdown/Dropdown';
import DatePicker from '../../ui/DatePicker/DatePicker';
import DateRangePicker from '../../ui/DateRangePicker/DateRangePicker';
import styles from './FilterPanel.module.css';

/**
 * FilterPanel Component
 *
 * @param {Object} props
 * @param {Array} props.filters - Filter definitions with structure:
 *   [{
 *     key: string,              // Unique identifier for filter
 *     label: string,            // Display label
 *     type: 'text' | 'select' | 'multiselect' | 'date' | 'daterange' | 'number' | 'custom',
 *     placeholder?: string,     // Placeholder text
 *     options?: Array,          // Options for select/multiselect type
 *     displayKey?: string,      // Key for display label (multiselect)
 *     valueKey?: string,        // Key for value (multiselect)
 *     searchKey?: string,       // Key for searching (multiselect)
 *     icon?: ReactElement,      // Icon component to display
 *     disabled?: boolean,       // Disable the filter
 *     hidden?: boolean,         // Hide the filter
 *     gridColumn?: string,      // Custom grid column span
 *     onChange?: function,      // Custom onChange handler
 *     dependsOn?: string,       // Key of filter this depends on
 *     getOptions?: function,    // Dynamic options based on other filters (filters) => options
 *     render?: function         // Custom render function for custom type
 *   }]
 * @param {Object} props.values - Current filter values as object { key: value }
 * @param {function} props.onChange - Callback when filter values change (auto-applies)
 * @param {function} props.onClear - Callback when Clear button clicked
 * @param {boolean} props.show - Control panel visibility
 * @param {function} props.onToggle - Callback when filter button is clicked
 * @param {function} props.onExport - Callback when export button is clicked
 * @param {boolean} props.showExport - Show export button (default: false)
 * @param {string} props.exportButtonText - Custom export button text
 * @param {Object} props.searchConfig - Search bar configuration
 * @param {Object} props.pageSizeConfig - Page size selector configuration
 */
const FilterPanel = ({
  filters = [],
  values = {},
  onChange,
  onClear,
  show = false,
  onToggle,
  onExport,
  showExport = false,
  exportButtonText = 'Export',
  searchConfig,
  pageSizeConfig
}) => {
  const [localValues, setLocalValues] = useState(values);
  const [searchValue, setSearchValue] = useState(
    searchConfig?.multiSearch && Array.isArray(searchConfig?.value)
      ? searchConfig.value
      : searchConfig?.value || ''
  );
  const [searchInputValue, setSearchInputValue] = useState('');
  const [showAllChips, setShowAllChips] = useState(false);
  const debounceTimerRef = useRef(null);
  const searchInputRef = useRef(null);

  const maxVisibleChips = searchConfig?.maxVisibleChips || 5;

  useEffect(() => {
    setLocalValues(values);
  }, [values]);

  useEffect(() => {
    if (searchConfig?.value !== undefined) {
      if (searchConfig.multiSearch && Array.isArray(searchConfig.value)) {
        setSearchValue(searchConfig.value);
        setSearchInputValue('');
      } else {
        setSearchValue(searchConfig.value);
        setSearchInputValue(searchConfig.value);
      }
    }
  }, [searchConfig?.value, searchConfig?.multiSearch]);

  // Handle filter change - auto applies
  const handleChange = (key, value, customOnChange) => {
    const newValues = { ...localValues, [key]: value };
    setLocalValues(newValues);

    if (customOnChange) customOnChange(value, newValues);
    if (onChange) onChange(newValues);
  };

  // Parse multi-search input
  const parseMultiSearchInput = (input) => {
    if (!input || input.trim() === '') return [];

    let values = [];
    if (input.includes('\t')) {
      values = input.split('\t');
    } else if (input.includes('\n')) {
      values = input.split('\n');
    } else if (input.includes(',')) {
      values = input.split(',');
    } else {
      values = [input];
    }

    return values
      .flatMap(v => v.split(/[\r\n]+/))
      .map(v => v.trim())
      .filter(v => v !== '');
  };

  // Add chip from current input
  const addChipFromInput = () => {
    if (!searchConfig?.multiSearch) return;

    const trimmedValue = searchInputValue.trim();
    if (trimmedValue === '') return;

    const currentChips = Array.isArray(searchValue) ? searchValue : [];

    if (!currentChips.includes(trimmedValue)) {
      const newChips = [...currentChips, trimmedValue];
      setSearchValue(newChips);
      setSearchInputValue('');

      if (searchConfig?.onChange) {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        searchConfig.onChange(newChips);
      }
    } else {
      setSearchInputValue('');
    }
  };

  // Handle key down in search input
  const handleSearchKeyDown = (e) => {
    if (!searchConfig?.multiSearch) return;

    if (e.key === 'Tab' || e.key === ',') {
      e.preventDefault();
      addChipFromInput();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      addChipFromInput();
    } else if (e.key === 'Backspace' && searchInputValue === '') {
      e.preventDefault();
      const currentChips = Array.isArray(searchValue) ? searchValue : [];
      if (currentChips.length > 0) {
        const newChips = currentChips.slice(0, -1);
        setSearchValue(newChips);
        if (searchConfig?.onChange) {
          if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
          searchConfig.onChange(newChips);
        }
      }
    }
  };

  // Handle search change with debounce
  const handleSearchChange = (value) => {
    if (searchConfig?.multiSearch) {
      setSearchInputValue(value);

      if (searchConfig?.onChange) {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
          const currentChips = Array.isArray(searchValue) ? searchValue : [];
          if (currentChips.length === 0 && value.trim() !== '') {
            searchConfig.onChange(value);
          } else if (currentChips.length === 0 && value.trim() === '') {
            searchConfig.onChange([]);
          }
        }, 500);
      }
    } else {
      setSearchValue(value);
      setSearchInputValue(value);

      if (searchConfig?.onChange) {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
          searchConfig.onChange(value);
        }, 500);
      }
    }
  };

  // Handle paste event for multi-search
  const handleSearchPaste = (e) => {
    if (!searchConfig?.multiSearch) return;

    const pastedText = e.clipboardData.getData('text');
    const parsedValues = parseMultiSearchInput(pastedText);

    if (parsedValues.length > 1) {
      e.preventDefault();
      const currentChips = Array.isArray(searchValue) ? searchValue : [];
      const newValues = parsedValues.filter(v => !currentChips.includes(v));
      const allChips = [...currentChips, ...newValues];

      setSearchValue(allChips);
      setSearchInputValue('');

      if (searchConfig?.onChange) {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        searchConfig.onChange(allChips);
      }
    }
  };

  // Remove a search chip
  const handleRemoveSearchChip = (chipValue) => {
    if (!searchConfig?.multiSearch || !Array.isArray(searchValue)) return;

    const newValues = searchValue.filter(v => v !== chipValue);
    setSearchValue(newValues);

    if (searchConfig?.onChange) {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      searchConfig.onChange(newValues);
    }
  };

  // Handle clear search
  const handleClearSearch = () => {
    const clearedValue = searchConfig?.multiSearch ? [] : '';
    setSearchValue(clearedValue);
    setSearchInputValue('');

    if (searchConfig?.onChange) {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      searchConfig.onChange(clearedValue);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (value) => {
    if (pageSizeConfig?.onChange) pageSizeConfig.onChange(parseInt(value));
  };

  // Handle clear
  const handleClear = () => {
    const clearedValues = {};
    filters.forEach(filter => {
      if (filter.type === 'daterange') {
        clearedValues[`${filter.key}From`] = '';
        clearedValues[`${filter.key}To`] = '';
      } else if (filter.type === 'multiselect') {
        clearedValues[filter.key] = [];
      } else {
        clearedValues[filter.key] = '';
      }
    });
    setLocalValues(clearedValues);
    if (onChange) onChange(clearedValues);
    if (onClear) onClear();
  };

  // Check if any filter has value
  const hasActiveFilters = () => {
    return Object.values(localValues).some(value => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== '' && value !== null && value !== undefined;
    });
  };

  // Get options for filter (static or dynamic)
  const getFilterOptions = (filter) => {
    if (filter.getOptions) return filter.getOptions(localValues);
    return filter.options || [];
  };

  // ─── MultiSelect Component ────────────────────────────────────────────────
  // Selections are kept in local `pendingValues` state while the dropdown is open.
  // They are only committed (propagated via onChangeProp) when the user clicks "Done".
  // Clicking outside discards pending changes and reverts to the last committed value.
  const MultiSelect = ({ filter, value, onChange: onChangeProp, isDisabled }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    // pendingValues mirrors what the user is currently clicking; not yet applied
    const [pendingValues, setPendingValues] = useState([]);
    const wrapperRef = useRef(null);

    const committedValues = Array.isArray(value) ? value : [];
    const options = getFilterOptions(filter);
    const displayKey = filter.displayKey || 'label';
    const valueKey = filter.valueKey || 'value';
    const searchKey = filter.searchKey || displayKey;

    // Sync pendingValues from committed whenever the dropdown opens
    const handleOpen = () => {
      if (isDisabled) return;
      setPendingValues(committedValues);
      setSearchTerm('');
      setIsOpen(true);
    };

    // Discard pending changes and close
    const handleDiscard = () => {
      setPendingValues(committedValues);
      setSearchTerm('');
      setIsOpen(false);
    };

    // Commit pending changes and close
    const handleDone = () => {
      onChangeProp(pendingValues);
      setSearchTerm('');
      setIsOpen(false);
    };

    // Close (discard) when clicking outside
    useEffect(() => {
      if (!isOpen) return;
      const handleClickOutside = (event) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
          handleDiscard();
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, committedValues]);

    const handleToggle = (optionValue) => {
      setPendingValues(prev =>
        prev.includes(optionValue)
          ? prev.filter(v => v !== optionValue)
          : [...prev, optionValue]
      );
    };

    const handleSelectAll = () => {
      setPendingValues(getFilteredOptions().map(opt => opt[valueKey]));
    };

    const handleClearAll = () => {
      setPendingValues([]);
    };

    const getFilteredOptions = () => {
      if (!searchTerm) return options;
      return options.filter(option =>
        option[searchKey]?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    };

    // Button label always reflects the last committed (applied) values
    const getButtonLabel = () => {
      if (committedValues.length === 0) return filter.placeholder || 'Select...';
      if (committedValues.length === 1) {
        const opt = options.find(o => o[valueKey] === committedValues[0]);
        return opt ? opt[displayKey] : committedValues[0];
      }
      return `${committedValues.length} selected`;
    };

    const filteredOptions = getFilteredOptions();
    const pendingCount = pendingValues.length;
    const hasPendingChanges = JSON.stringify([...pendingValues].sort()) !== JSON.stringify([...committedValues].sort());

    return (
      <div ref={wrapperRef} className={styles.multiselectWrapper}>
        <button
          type="button"
          onClick={isOpen ? handleDiscard : handleOpen}
          disabled={isDisabled}
          className={`${styles.multiselectButton} ${isOpen ? styles.open : ''} ${committedValues.length > 0 ? styles.hasValue : ''}`}
        >
          <span className={styles.multiselectLabel}>{getButtonLabel()}</span>
          <ChevronDown
            className={`${styles.multiselectArrow} ${isOpen ? styles.rotated : ''}`}
            size={18}
          />
        </button>

        {isOpen && (
          <div className={styles.multiselectDropdown}>
            {/* Search Input */}
            {options.length > 5 && (
              <div className={styles.multiselectSearch}>
                <Search size={14} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={filter.searchPlaceholder || 'Search...'}
                  className={styles.multiselectSearchInput}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  autoFocus
                />
              </div>
            )}

            {/* Select All / Clear All */}
            {filteredOptions.length > 0 && (
              <div className={styles.multiselectActions}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleSelectAll}
                  className={styles.multiselectActionButton}
                >
                  Select All
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleClearAll}
                  className={styles.multiselectActionButton}
                >
                  Clear
                </button>
              </div>
            )}

            {/* Options List */}
            <div className={styles.multiselectOptions}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => {
                  const optionValue = option[valueKey];
                  const isSelected = pendingValues.includes(optionValue);

                  return (
                    <label
                      key={optionValue}
                      className={`${styles.multiselectOption} ${isSelected ? styles.selected : ''}`}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggle(optionValue)}
                        className={styles.multiselectCheckbox}
                      />
                      <span className={styles.multiselectCheckboxCustom}>
                        {isSelected && <Check size={14} />}
                      </span>
                      <span className={styles.multiselectOptionLabel}>
                        {option[displayKey]}
                      </span>
                    </label>
                  );
                })
              ) : (
                <div className={styles.multiselectEmpty}>No options found</div>
              )}
            </div>

            {/* Footer: Cancel + Done */}
            <div className={styles.multiselectFooter}>
              {pendingCount > 0 && (
                <span className={styles.multiselectPendingCount}>
                  {pendingCount} selected
                </span>
              )}
              <div className={styles.multiselectFooterActions}>
                <button
                  type="button"
                  onClick={handleDiscard}
                  className={styles.multiselectCancelButton}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDone}
                  className={`${styles.multiselectCloseButton} ${hasPendingChanges ? styles.multiselectCloseButtonActive : ''}`}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Render filter input based on type ───────────────────────────────────
  const renderFilterInput = (filter) => {
    const value = localValues[filter.key] || (filter.type === 'multiselect' ? [] : '');
    const isDisabled = filter.disabled || (filter.dependsOn && !localValues[filter.dependsOn]);

    if (filter.type === 'custom' && filter.render) {
      return filter.render(value, (newValue) => handleChange(filter.key, newValue, filter.onChange));
    }

    if (filter.type === 'number') {
      return (
        <div className={styles.inputWrapper}>
          {filter.icon && <div className={styles.inputIcon}>{filter.icon}</div>}
          <input
            type="number"
            value={value}
            onChange={(e) => handleChange(filter.key, e.target.value, filter.onChange)}
            placeholder={filter.placeholder}
            className={`${styles.input} ${filter.icon ? styles.withIcon : ''}`}
            disabled={isDisabled}
            min={filter.min}
            max={filter.max}
            step={filter.step}
          />
        </div>
      );
    }

    switch (filter.type) {
      case 'text':
        return (
          <div className={styles.inputWrapper}>
            {filter.icon && <div className={styles.inputIcon}>{filter.icon}</div>}
            <input
              type="text"
              value={value}
              onChange={(e) => handleChange(filter.key, e.target.value, filter.onChange)}
              placeholder={filter.placeholder}
              className={`${styles.input} ${filter.icon ? styles.withIcon : ''}`}
              disabled={isDisabled}
            />
          </div>
        );

      case 'select':
        const options = getFilterOptions(filter);
        return (
          <Dropdown
            value={value}
            onChange={(newValue) => handleChange(filter.key, newValue || '', filter.onChange)}
            options={options}
            placeholder={filter.placeholder || 'Select...'}
            searchable={filter.searchable}
            disabled={isDisabled}
            allowClear
          />
        );

      case 'multiselect':
        return (
          <MultiSelect
            filter={filter}
            value={value}
            onChange={(newValue) => handleChange(filter.key, newValue, filter.onChange)}
            isDisabled={isDisabled}
          />
        );

      case 'date':
        return (
          <DatePicker
            value={value}
            onChange={(newValue) => handleChange(filter.key, newValue || '', filter.onChange)}
            placeholder={filter.placeholder || 'Select date'}
            disabled={isDisabled}
          />
        );

      case 'daterange':
        const fromKey = `${filter.key}From`;
        const toKey = `${filter.key}To`;
        const fromValue = localValues[fromKey] || '';
        const toValue = localValues[toKey] || '';

        return (
          <DateRangePicker
            value={{ from: fromValue, to: toValue }}
            onChange={({ from, to }) => {
              const newValues = { ...localValues, [fromKey]: from || '', [toKey]: to || '' };
              setLocalValues(newValues);
              if (filter.onChange) filter.onChange({ from, to }, newValues);
              if (onChange) onChange(newValues);
            }}
            placeholder={filter.placeholder || 'Select date range'}
            disabled={isDisabled}
          />
        );

      default:
        return null;
    }
  };

  // Filter visible filters
  const visibleFilters = filters.filter(f => !f.hidden);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const hasActiveSearchValues = searchConfig?.multiSearch
    ? (Array.isArray(searchValue) && searchValue.length > 0) || searchInputValue.trim() !== ''
    : searchValue !== '';

  const chipsArray = Array.isArray(searchValue) ? searchValue : [];
  const visibleChips = showAllChips ? chipsArray : chipsArray.slice(0, maxVisibleChips);
  const hiddenChipsCount = chipsArray.length - maxVisibleChips;

  return (
    <div className={styles.filtersSection}>
      <div className={styles.filtersRow}>
        {/* Search Bar */}
        {searchConfig?.show && (
          <div className={styles.searchBox}>
            <Search className={styles.searchIcon} size={18} />

            <div className={`${styles.searchInputContainer} ${chipsArray.length > 0 ? styles.hasChips : ''}`}>
              {searchConfig.multiSearch && chipsArray.length > 0 && (
                <div className={styles.searchChips}>
                  {visibleChips.map((chip, index) => (
                    <div key={index} className={styles.searchChip}>
                      <span title={chip}>{chip}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveSearchChip(chip)}
                        className={styles.searchChipRemove}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}

                  {!showAllChips && hiddenChipsCount > 0 && (
                    <div
                      className={`${styles.searchChip} ${styles.searchChipMore}`}
                      onClick={() => setShowAllChips(true)}
                    >
                      <span>+{hiddenChipsCount} more</span>
                    </div>
                  )}

                  {showAllChips && chipsArray.length > maxVisibleChips && (
                    <div
                      className={`${styles.searchChip} ${styles.searchChipMore}`}
                      onClick={() => setShowAllChips(false)}
                    >
                      <span>Show less</span>
                    </div>
                  )}
                </div>
              )}

              <input
                ref={searchInputRef}
                type="text"
                value={searchInputValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                onPaste={handleSearchPaste}
                placeholder={
                  searchConfig.multiSearch && chipsArray.length > 0
                    ? 'Type and press Tab or comma...'
                    : searchConfig.placeholder || 'Search...'
                }
                className={styles.searchInput}
              />
            </div>

            {hasActiveSearchValues && (
              <button
                onClick={handleClearSearch}
                className={styles.clearSearchButton}
                type="button"
                title="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className={styles.actionButtons}>
          <button
            onClick={onToggle}
            className={`${styles.filterButton} ${show ? styles.active : ''}`}
            type="button"
          >
            <Filter size={18} />
            <span className={styles.buttonText}>Filters</span>
            {hasActiveFilters() && <span className={styles.filterBadge} />}
          </button>

          {showExport && onExport && (
            <button
              onClick={onExport}
              className={styles.exportButton}
              type="button"
            >
              <FileSpreadsheet size={18} />
              <span className={styles.buttonText}>{exportButtonText}</span>
            </button>
          )}

          {pageSizeConfig?.show && (
            <div className={styles.pageSizeSelector}>
              <label>Show</label>
              <select
                value={pageSizeConfig.value}
                onChange={(e) => handlePageSizeChange(e.target.value)}
                className={styles.pageSizeSelect}
              >
                {pageSizeConfig.options?.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {show && (
        <div className={styles.filtersPanel} data-floating-dropdowns>
          <div className={styles.filterGrid}>
            {visibleFilters.map((filter) => (
              <div
                key={filter.key}
                className={styles.filterGroup}
                style={filter.gridColumn ? { gridColumn: filter.gridColumn } : {}}
              >
                <label htmlFor={filter.key}>{filter.label}</label>
                {renderFilterInput(filter)}
              </div>
            ))}
          </div>

          {hasActiveFilters() && (
            <button
              onClick={handleClear}
              className={styles.clearFiltersButton}
              type="button"
            >
              <X size={16} />
              Clear All Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
